/**
 * QuickstartAutoRedirect Component
 * 
 * Minimal auto-redirect page for seamless store redirects.
 * Shows ONLY the blurred seamless video background with no UI elements.
 * Auto-redirects to Steam store after 500ms.
 * 
 * Used when autoRedirectToCTA is enabled for:
 * - Successful exits (in-game wishlist button click) - triggerSource: 'auto_redirect'
 * - Timer expirations (AFK timeout) - triggerSource: 'afk_timer_hit'
 * - Timer expirations (ad timer maxed out) - triggerSource: 'maxed_out_game_time'
 * - Error states (matchmaking/connection failures) - triggerSource: specific error
 * 
 * Visual: Just the seamless loading video with blur effect, no logos/text/buttons
 */

import { useEffect, useRef } from 'react';
import { QuickstartTheme, getResolvedCtaUrl } from '../types/theme';
import { getOutboundCtaUrl } from '../utils/steamUrls';

interface QuickstartAutoRedirectProps {
  theme: QuickstartTheme;
  hasSlugConfig: boolean;
  /** Callback to fire analytics before redirect (should send Wishlist CTA Clicked event) */
  onRedirect?: () => void;
}

export const QuickstartAutoRedirect = ({ 
  theme, 
  hasSlugConfig,
  onRedirect 
}: QuickstartAutoRedirectProps) => {
  const hasRedirected = useRef(false);

  useEffect(() => {
    const ctaUrl = getResolvedCtaUrl(theme);
    if (hasRedirected.current || !ctaUrl) {
      return;
    }

    hasRedirected.current = true;
    const finalUrl = getOutboundCtaUrl(ctaUrl, hasSlugConfig);

    // Send analytics callback before redirect (fires Wishlist CTA Clicked event)
    if (onRedirect) {
      onRedirect();
    }

    // 500ms delay to ensure analytics are sent
    // Analytics uses fetch with keepalive:true, which helps during redirects
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [theme, onRedirect]);

  // Return empty div - seamless background with blur shows through
  // No logos, no text, no buttons - just the blurred video
  return <div className='fixed inset-0' />;
};
